/* eslint-disable react/no-danger */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import ReactQuill from 'react-quill';
import { Button } from 'react-bootstrap';
import Select from 'react-select';

import { useTheme, makeStyles } from '@material-ui/core/styles';

import Card from '@material-ui/core/Card';
import Paper from '@material-ui/core/Paper';
import { Alert } from '@material-ui/lab'

import {
  Findings as FindingActions,
  Message as MessageActions,
}
  from '../../../redux/actions';
import Workflow from './Workflow';
import {
  Flags,
  findingType,
  constantsType,
  MESSAGE_ERROR_TEXT,
  shortIdStr,
} from './common';
import { setOwners } from '../../../redux/actions/Findings';
import FindingMessages from './Messages';
import FindingDetails from './Detail';
import { LabelsAndTags, TAG_OBJECT_TYPES } from '../Label/Label';
import ErrorBoundary from '../ErrorBoundary';
import { sanitize, sortByName } from '../../../lib/helpers';
import { pageWithPayloadAndQuery } from '../../../redux/actions/Page';
import FindingSupport from './FindingSupport';

import './FullWidth.sass';
import FindingAttachmentsView from "./Attachments";

import {Message as MessageModel} from 'lib/models/Message';

// Pulled from
// https://github.com/quilljs/quill/issues/861#issuecomment-239961806
const Block = ReactQuill.Quill.import('blots/block');
Block.tagName = 'DIV';
ReactQuill.Quill.register(Block, true);

const editorFormats = [
  'font',
  'bold',
  'italic',
  'underline',
  'blockquote',
  'code-block',
  'list',
  'indent',
];

const editorModules = {
  toolbar: [
    [{ font: [] }, 'bold', 'italic', 'underline', 'blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
  ],
  clipboard: {
    // toggle to add extra line breaks when pasting HTML:
    matchVisual: true,
  },
};

const OwnerSelector = ({
  onChange, users = [], owners = [], currentUser, isAddingOwner
}) => {
  // Make the 'self' option bold
  const optionStyles = {
    option: (styles, { data }) => ({
      ...styles,
      color: 'black',
      fontWeight: data.fontWeight,
    }),
  };

  const optionFromUser = (user, self = false) => ({
    value: user.id,
    label:
      `${user.first_name} ${user.last_name}${self === true ? ' (You)' : ''}`,
    name: `${user.first_name} ${user.last_name}`,
    fontWeight: `${self === true ? 'bold' : 'normal'}`,
  });

  const loggedinUser = users.find(user => user.id === currentUser);

  let options = loggedinUser ? [optionFromUser(loggedinUser, true)] : [];

  options = options.concat(
    users
      .filter(user => user.id !== currentUser)
      .map(optionFromUser)
      .sort(sortByName)
  );

  return (
    <div className="owner-selector">
      <Select
        value={options.filter(i => owners.indexOf(i.value) > -1)}
        isMulti
        name="colors"
        options={options}
        isLoading={isAddingOwner}
        className="basic-multi-select"
        classNamePrefix="select"
        onChange={onChange}
        styles={optionStyles}
      />
    </div>
  );
};

OwnerSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  owners: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentUser: PropTypes.string,
};

OwnerSelector.defaultProps = {
  currentUser: '',
};

const ScrollIndicator = () => (
  <div className="scroll-indicator-h" />
);

const useStyles = makeStyles((theme) => ({
  quill: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    '& span': {
      color: theme.palette.text.primary
    }
  }
}))

/* eslint-disable no-unused-vars */
const Finding = ({
  onChangeOwner,
  onToggleTextbox,
  onFieldChange,
  onSaveComment,
  gotoFinding,
  constants,
  currentUser,
  superadmin,
  orgUsers,
  pageRole,
  finding: {
    analysis,
    attachments,
    blocked,
    category: categoryId,
    created,
    createdBy,
    evidence = [],
    id,
    isSample,
    jurisdiction,
    messageError,
    name,
    orgId,
    owners,
    pendingAnswer,
    pendingMessage,
    priority,
    relatedFindings,
    shortId,
    showingTextBox,
    type: typeId,
    status,
    statusModified,
    srcCountry = [],
    summary,
    workflows,
  },
  query,
  gotoBlocks,
}) => {
  const theme = useTheme()
  const classes = useStyles()

  const [messageModel, setMessageModel] = useState({})
  const [ownersForRole, setOwnersForRole] = useState([])
  const [isAddingOwner, setIsAddingOwner] = useState(false)
  const [hasWorkflowError, setWorkflowError] = useState(false)
  const [valuesForResponders, setValuesForResponders] = useState([])

  useEffect(() => {
    if(ownersForRole !== owners[`${pageRole}s`]) {
      setIsAddingOwner(false)
    }
    setOwnersForRole(owners[`${pageRole}s`])
  }, [owners])

  useEffect(() => {
    if(!!messageModel?.body && attachments.length)
      toggleTextbox()
  }, [attachments])

  const handleChangeOwner = values => {
    setIsAddingOwner(true)
    onChangeOwner(values)
    setValuesForResponders(values)
  }

  const handleAddSelf = () => {
    // value needed for change owner func is user id
    const selfValue = { value: currentUser }

    // if there are existing responders, include
    let newValues = valuesForResponders ? [...valuesForResponders, selfValue] : [selfValue]

    // call owner change with relevant values
    handleChangeOwner(newValues)
  }

  const toggleTextbox = () => onToggleTextbox(id);

  const newMessageConstants = {
    type: 20,
    orgId: orgId,
    foreignId: id,
    asynchronous: true,
    sender: currentUser,
    foreignType: 'FINDING_COMMENT'
  }

  const updatePendingMessage = (value) => {
    messageModel.set({body: value})
  }

  const handleNewMessage = () => {
    setMessageModel(new MessageModel(newMessageConstants))
    toggleTextbox()
  }

  const statusName =
    status !== undefined
    && status !== null
    && constants.status
    && constants.status.fromId(status);

  const cancelTextbox = () => {
    onFieldChange({ id, value: false, field: 'messageError' });
    onFieldChange({ id, value: '', field: 'pendingMessage' });
    toggleTextbox();
  };

  const submitMessage = () => {
    onSaveComment(messageModel);
    toggleTextbox();
  };

  const type = constants.type.fromId(typeId, '');
  const category = constants.category.fromId(categoryId, '');

  const ownerIds = Object.values(owners).reduce((all, x) => all.concat(x), []);

  const uncompletedWorkflows = workflows
    .filter(w => !w.completed)
    .map((workflow, key) => (
      <Workflow
        key={key}
        findingId={id}
        workflow={workflow}
        pageRole={pageRole}
        pendingAnswer={pendingAnswer}
        onChangeOwner={() => handleAddSelf()}
        userIsOwner={ownerIds.includes(currentUser)}
        onWorkflowError={() => setWorkflowError(true)}
      />
    ));

  const completedWorkflows = workflows
    .filter(w => w.completed)
    .map((workflow, key) => (
      <Workflow
        pendingAnswer={null}
        key={key}
        findingId={id}
        workflow={workflow}
        pageRole={pageRole}
        userIsOwner={ownerIds.includes(currentUser)}
        onWorkflowError={() => setWorkflowError(true)}
      />
    ));

  if (hasWorkflowError) {
    return <span />;
  }

  // TODO ReactQuill could use a wrapper. Modules.toolbar needs to pass through
  // Also, the way dispatch functions are here could be fixed. No need to pass
  // findingId + UserID should be retrieved from store!

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <ErrorBoundary>
      <div className="full-width-finding">
        <header>
          <div className="header-first-row">
            <div className="name">{name}</div>
            <div className="short-id">{shortIdStr(shortId)}</div>

            {blocked && (
            <div className="blocklists-btn-container">
              <Button
                onClick={gotoBlocks({ orgId, findingId: id, query })}
                className="view-blocklists-btn"
              >
              View Blocklists
              </Button>
            </div>
            )}
          </div>

          <div className="finding-tags">
            <LabelsAndTags
              objId={id}
              objType={TAG_OBJECT_TYPES.FINDING}
              orgId={orgId}
              superadmin={superadmin}
            />
          </div>
        </header>

        { isSample ?
          <Alert
            severity={'info'}
            className='sampleFindingWarning'
            >
            This is an example finding you can use to learn what Findings look like and how to respond to them.
            <br />
            The activity in this sample finding is not real and does not reflect data your organization sent to Blumira.
          </Alert>
          : null
        }

        <Paper>
          <div id="main" className="main-content">
            <div className={blocked ? 'info info-blocked' : 'info'}>
              <div className="left">
                <div className="general">
                  <div className="type">{category}</div>
                  <Flags
                    priority={priority}
                    typeLabel={type}
                    typeId={typeId}
                    fullWidth
                  />
                  <div className="date">
                    <div>
                      <span className="infoTitle">Date Created</span>
                      {moment
                        .utc(created)
                        .tz(moment.tz.guess(true))
                        .format('lll z')}
                    </div>
                  </div>
                </div>

                {statusName && (
                  <div className="status">
                    <span className="infoTitle">Status</span>
                    {statusName}
                  </div>
                )}

                {!blocked && (
                  <div className="owners">
                    <span className="infoTitle">{`Assigned ${pageRole}s`}</span>
                    <OwnerSelector
                      users={orgUsers}
                      owners={ownersForRole}
                      currentUser={currentUser}
                      onChange={handleChangeOwner}
                      isAddingOwner={isAddingOwner}
                    />
                  </div>
                )}

                <div className="analysis">
                  <div className="infoTitle">analysis</div>
                  <div
                    style={{ color: theme.palette.text.primary }}
                    dangerouslySetInnerHTML={sanitize(analysis, {
                      html: true,
                      safe: true,
                    })}
                  />
                </div>
              </div>

              {!blocked && (
                <div className="workflows">
                  <div className="infoTitle">workflows</div>
                  <div className="workflowContainer">{uncompletedWorkflows}</div>
                  <div className="completedWorkflows">{completedWorkflows}</div>
                </div>
              )}
            </div>
          </div>
        </Paper>

        <Card>
          <section id="usercontent">
            <div className="messages">
              <div className="composer">
                {showingTextBox ? (
                  <div>
                    {messageError && (
                      <div className="message-error">
                        {MESSAGE_ERROR_TEXT}
                      </div>
                    )}

                    <ReactQuill
                      className={classes.quill}
                      formats={editorFormats}
                      modules={editorModules}
                      placeholder=""
                      defaultValue={pendingMessage}
                      onChange={updatePendingMessage}
                      value={messageModel?.body ? messageModel?.body : ''}
                    />
                    <br />
                    <Button bsStyle="danger" onClick={cancelTextbox}>
                      Cancel
                    </Button>
                    <Button bsStyle="primary" onClick={submitMessage} style={{marginLeft: 8}}>
                      Send
                    </Button>
                  </div>
                ) : (
                  <span
                    className="placeholder"
                    tabIndex="0"
                    role="button"
                    onClick={handleNewMessage}
                  >
                    Write a message...
                  </span>
                )}
              </div>
              <FindingMessages findingId={id} />
            </div>
            <div className="attachments">
              <FindingAttachmentsView
                findingId={id}
                orgId={orgId}
                attachments={attachments}
              />
            </div>
          </section>
        </Card>

        <Card style={{ marginTop: 20 }}>
          <section id="evidence" className="evidence">
            <ScrollIndicator />
            <FindingDetails
              {...{
                evidence,
                srcCountry,
                summary,
                status,
                statusModified,
                relatedFindings,
              }}
            />
          </section>
        </Card>


        {superadmin && (
          <Card style={{ marginTop: 20 }}>
            <section className="finding-support">
              <div className="blu-support-header">
                Support
              </div>

              <FindingSupport />
            </section>
          </Card>
        )}

      </div>
    </ErrorBoundary>
  );
};

Finding.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  constants: constantsType.isRequired,
  finding: findingType.isRequired,
  gotoFinding: PropTypes.func.isRequired,
  onChangeOwner: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSaveComment: PropTypes.func.isRequired,
  onToggleTextbox: PropTypes.func.isRequired,
  orgUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  pageRole: PropTypes.string,
  gotoBlocks: PropTypes.func.isRequired,
  query: PropTypes.shape({}).isRequired,
  superadmin: PropTypes.bool.isRequired,
  currentUser: PropTypes.string.isRequired,
};

Finding.defaultProps = {
  pageRole: 'responder',
};

const mapStateToProps = (state, ownProps) => ({
  constants: state.findings.get('constants'),
  comments: state.message.getIn(['messages', ownProps.finding.id], []),
  currentUser: state.session.settings.user.id,
  superadmin:
    (state.session.settings.user &&
      state.session.settings.user.superadmin) ||
    false,
  query: state.location.query || {},
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  onToggleTextbox: id => dispatch(FindingActions.toggleTextbox({ id })),
  onFieldChange: ({ id, field, value }) =>
    dispatch(FindingActions.updateField({ id, field, value })),
  onSaveComment: (message) => {
    dispatch(MessageActions.saveCommentForFinding(message))
  },
  fetchComments: () =>
    dispatch(
      MessageActions.getCommentsForFinding({
        findingId: ownProps.finding.id,
      })
    ),
  onChangeOwner: (values) => {
    dispatch(
      setOwners({
        findingId: ownProps.finding.id,
        owners: (values || []).map(i => i.value),
        ownerType: ownProps.pageRole || 'responder',
      })
    );
  },
  gotoFinding: (id, orgId) =>
    dispatch({
      type: 'PAGE',
      payload: {
        orgId,
        toplevel: 'reporting',
        secondlevel: 'findings',
        id1: id,
      },
    }),

  gotoBlocks: ({ orgId, findingId, query = {} }) => (evt) => {
    evt.stopPropagation();

    const payload = {
      orgId,
      toplevel: 'settings',
      secondlevel: 'blocklists',
    };

    const newQuery = {
      ...query,
      findingId,
    };

    dispatch(pageWithPayloadAndQuery({
      payload,
      query: newQuery,
    }));
  },

});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Finding);
